import { cloneDeep } from "lodash";
import { withHistory } from "../../../../redux/actions/history";
import { setWheelSegments } from "../../../../redux/reducers/segmentBuilder";
import {
  getSelectedSegment,
  getWheelSegments,
} from "../../../../redux/selectors/segmentBuilderSelectors";

/**
 * T22.91 (stgfix-10101337-T2291-auto): "Video won't load if you leave the
 * Segment and come back."
 *
 * MEASURED live on web-stg.iyuga.net (main.a491d0fd.js): open a Segment's
 * Video block, start an Upload, click another Segment while the upload is
 * still running, come back -> the block sits on the "Loading video" skeleton
 * forever, and the server copy of the block ends up with NO `src` (the exact
 * state Kelly's /builder/183922 Segment 2 is in). Staying on the Segment for
 * the same upload persists the src fine (positive control).
 *
 * Root cause: UploadMedia hands the finished upload back through
 * `handleMedia` -> VideoSettings' craft `setProp`, a closure bound to a node
 * of the craft editor. A Segment switch deserializes a DIFFERENT Segment
 * into that editor, so by the time the upload resolves the node is gone and
 * the write is silently dropped — while the `isLoading: true` written at
 * upload START was already flushed into the Segment's saved nodes.
 *
 * Fix: decide at COMPLETION time, from live Redux state, where the block
 * lives. Still the open Segment -> the original craft `setProp` path,
 * unchanged. A different Segment is open -> patch the block's props inside
 * the owning Segment's persisted `nodes` (the copy the canvas reloads from on
 * return, and the one autosave sends), recorded via `withHistory` so it reads
 * as a real edit and gets saved.
 */

/** The single source of truth for what a picked/uploaded video does to the block's props. */
export const applyVideoMediaProps = (props, value) => {
  // T22.170: the upload-start (`{ id, loading: true }`) and cancel/failure
  // (`{ id, loading: false }`) payloads carry no `src`. Writing `value.src`
  // unconditionally wiped the block's existing video the moment an upload
  // began, so a Cancel (or a failed upload) left the block empty. Only a
  // payload that actually names a video replaces it.
  if (Object.prototype.hasOwnProperty.call(value, "src")) {
    props.src = value.src;
    props.thumbnail = value.thumbnail;
  }
  // Set loading state based on the uploaded file data only if loading key exists
  if (Object.prototype.hasOwnProperty.call(value, "loading")) {
    props.isLoading = value.loading !== false;
  }
};

/**
 * Returns a NEW wheel_segments array with `mutate(props)` applied to node
 * `nodeId` of Segment `segmentId`, or null when that Segment/node is not
 * found (nothing safe to write). Preserves the `nodes` storage shape (JSON
 * string vs object). Never mutates the input.
 */
export const patchSegmentNodeProps = (wheelSegments, segmentId, nodeId, mutate) => {
  if (!Array.isArray(wheelSegments) || segmentId == null || nodeId == null) {
    return null;
  }
  let patched = false;
  const next = wheelSegments.map((segment) => {
    if (!segment || segment.id !== segmentId || patched) return segment;
    const isString = typeof segment.nodes === "string";
    let nodeMap;
    try {
      nodeMap = isString ? JSON.parse(segment.nodes) : cloneDeep(segment.nodes);
    } catch (e) {
      return segment;
    }
    const node = nodeMap && nodeMap[nodeId];
    if (!node) return segment;
    node.props = { ...(node.props || {}) };
    mutate(node.props);
    patched = true;
    return {
      ...segment,
      nodes: isString ? JSON.stringify(nodeMap) : nodeMap,
    };
  });
  return patched ? next : null;
};

/**
 * Thunk: write a video result to the block it belongs to.
 *
 * @param {object} args
 * @param {*} args.originSegmentId  the Segment that was open when the pick/upload started
 * @param {string} args.nodeId      the craft node id of the Video block
 * @param {object} args.value       the handleMedia payload ({ src, thumbnail, loading, ... })
 * @param {Function} args.applyLive the original craft setProp write, used while the Segment is still open
 */
export const writeVideoMediaToOwningSegment =
  ({ originSegmentId, nodeId, value, applyLive }) =>
  (dispatch, getState) => {
    const liveSelectedId = getSelectedSegment(getState())?.id ?? null;
    if (originSegmentId == null || liveSelectedId === originSegmentId) {
      applyLive();
      return;
    }
    const patched = patchSegmentNodeProps(
      getWheelSegments(getState()),
      originSegmentId,
      nodeId,
      (props) => applyVideoMediaProps(props, value)
    );
    if (patched) dispatch(withHistory(setWheelSegments(patched)));
  };
